/* SS Cookware combo picker — size <-> tile sync + ATC target swap.
   Web component <sscw-picker> registered once globally.
   Behavior:
     - Switching size hides all combo sets and shows the matching set
     - Selecting a tile updates the hidden <input name="id"> for the product form
     - When the selected tile's price is known, updates the [data-sscw-total] node
     - Submits via the existing buy-buttons form (no fetch hijack); Shopify accepts
       cross-product variant IDs at /cart/add
*/

(function () {
  if (window.customElements && window.customElements.get('sscw-picker')) return;

  class SscwPicker extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('data-form-id');
      this.idInput = this.querySelector('[data-sscw-id-input]');
      this.stage = this.querySelector('[data-sscw-stage]');
      this.selectedSizeLabel = this.querySelector('[data-sscw-selected-size]');

      this._onSizeChange = this._onSizeChange.bind(this);
      this._onTileChange = this._onTileChange.bind(this);

      this.addEventListener('change', (e) => {
        const t = e.target;
        if (!t) return;
        if (t.classList && t.classList.contains('sscw-size__radio')) {
          this._onSizeChange(t);
        } else if (t.hasAttribute && t.hasAttribute('data-sscw-tile-radio')) {
          this._onTileChange(t);
        }
      });

      // Initialise to current state
      const activeSizeRadio = this.querySelector('.sscw-size__radio:checked');
      if (activeSizeRadio) this._onSizeChange(activeSizeRadio, { silent: true });

      const activeSet = this.stage.querySelector('.sscw-combo__set.is-active');
      if (activeSet) {
        const defaultTile = activeSet.querySelector('[data-sscw-tile-radio]:checked')
          || activeSet.querySelector('[data-sscw-tile-radio]');
        if (defaultTile) {
          if (!defaultTile.checked) defaultTile.checked = true;
          this._onTileChange(defaultTile, { silent: true });
        }
      }
    }

    _onSizeChange(radio, opts) {
      const variantId = radio.getAttribute('data-variant-id');
      if (!variantId) return;

      if (this.selectedSizeLabel) {
        this.selectedSizeLabel.textContent = radio.value || '';
      }

      // Dispatch variant:change on the form so the theme's SalePrice / CompareAtPrice
      // / on-sale-badge / sold-out-badge custom elements update themselves.
      this._dispatchVariantChange({
        id: parseInt(variantId, 10),
        price: parseInt(radio.getAttribute('data-variant-price') || '0', 10),
        compare_at_price: parseInt(radio.getAttribute('data-variant-compare') || '0', 10) || null,
        available: radio.getAttribute('data-variant-available') !== 'false',
        title: radio.value || ''
      });

      const sets = this.stage.querySelectorAll('[data-sscw-set]');
      let activeSet = null;
      sets.forEach((s) => {
        const match = s.getAttribute('data-variant-id') === variantId;
        s.classList.toggle('is-active', match);
        if (match) {
          s.removeAttribute('hidden');
          activeSet = s;
        } else {
          s.setAttribute('hidden', '');
        }
      });

      // Default-select a tile inside the newly active set (middle if 3, last if 2, only if 1)
      if (activeSet) {
        let target = activeSet.querySelector('[data-sscw-tile-radio]:checked');
        if (!target) {
          const all = activeSet.querySelectorAll('[data-sscw-tile-radio]');
          // CRO: default to the FIRST tile ("Just the Pan", lowest price) on size switch,
          // matching the ad's frypan promise. (Was the middle/highest-AOV tile.)
          if (all.length >= 1) target = all[0];
        }
        if (target) {
          target.checked = true;
          this._onTileChange(target, { silent: opts && opts.silent });
        } else {
          // No tiles — fall back to the size variant id directly on the form
          this._setFormVariant(variantId);
        }
      }
    }

    _onTileChange(radio, opts) {
      const targetVariantId = radio.getAttribute('data-target-variant-id');
      const targetAvailable = radio.getAttribute('data-target-available') !== 'false';

      if (!targetVariantId) {
        // Tile points at a product with no available variant — fall back to size variant id
        const activeSize = this.querySelector('.sscw-size__radio:checked');
        if (activeSize) this._setFormVariant(activeSize.getAttribute('data-variant-id'));
        this._updateATCAvailability(false);
        return;
      }
      this._setFormVariant(targetVariantId);
      this._updateATCAvailability(targetAvailable);
      this._swapHeroImage(radio);

      // Update page-level price by dispatching variant:change with this tile's data.
      this._dispatchVariantChange({
        id: parseInt(targetVariantId, 10),
        price: parseInt(radio.getAttribute('data-price') || '0', 10),
        compare_at_price: parseInt(radio.getAttribute('data-compare') || '0', 10) || null,
        available: targetAvailable,
        title: ''
      });

      // Broadcast a custom event for any other widgets that want to react.
      this.dispatchEvent(new CustomEvent('sscw:tile-changed', {
        bubbles: true,
        detail: {
          variantId: targetVariantId,
          available: targetAvailable,
          price: radio.getAttribute('data-price'),
          compare: radio.getAttribute('data-compare'),
          productUrl: radio.getAttribute('data-target-product-url'),
          productHandle: radio.getAttribute('data-target-product-handle')
        }
      }));
    }

    /* Show the selected bundle's image over the gallery so the customer can see what's in the
       combo. Bundles point at OTHER products whose images aren't in this product's gallery, and
       the first gallery slide is a VIDEO — so swapping a slide's src is unreliable (the carousel
       snaps back to the video). Instead we overlay the tile image on the gallery stage. Selecting
       "Just the Pan" (a tile that points back at this product) hides the overlay to restore the
       native gallery/video. */
    _swapHeroImage(radio) {
      try {
        // Attach to the aspect-ratio wrapper — it is the element constrained to the visible hero
        // height (270px on mobile via C2). The media-list-wrapper is taller (square) and would
        // make the overlay spill down over the feature-highlight icons below the gallery.
        const stage = document.querySelector('.product-gallery .product-gallery__ar-wrapper')
          || document.querySelector('.product-gallery__ar-wrapper')
          || document.querySelector('.product-gallery .product-gallery__media-list-wrapper')
          || document.querySelector('.product-gallery__media-list-wrapper')
          || document.querySelector('.product-gallery');
        if (!stage) return;

        // Create the overlay once.
        let overlay = this._heroOverlay;
        if (!overlay) {
          overlay = document.createElement('img');
          overlay.className = 'sscw-hero-overlay';
          overlay.alt = '';
          overlay.setAttribute('aria-hidden', 'true');
          overlay.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;'
            + 'z-index:4;border-radius:16px;background:#fff;display:none;';
          this._heroOverlay = overlay;
        }
        // Re-parent on EVERY call: the media-carousel renders its ar-wrapper late, so on the
        // initial (page-load) call the stage resolves to a fallback (taller media-list-wrapper).
        // Moving it to the current preferred stage each time lands it in the 270px ar-wrapper once
        // that exists — otherwise the overlay spills over the feature icons below the gallery.
        if (overlay.parentElement !== stage) {
          if (getComputedStyle(stage).position === 'static') stage.style.position = 'relative';
          stage.appendChild(overlay);
        }

        // A tile that points back at this product (e.g. "Just the Pan") → restore native gallery.
        const productHandle = this.getAttribute('data-product-handle');
        const tileHandle = radio.getAttribute('data-target-product-handle');
        const tileImg = this.querySelector('label[for="' + radio.id + '"] .sscw-tile__img');
        if (!tileImg || !tileHandle || tileHandle === productHandle) {
          overlay.style.display = 'none';
          return;
        }

        // Build a crisp hero-sized URL from the tile image (bump Shopify width param).
        let src = tileImg.getAttribute('src') || '';
        src = /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$1900')
                                        : src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=900';
        overlay.src = src;
        overlay.style.display = 'block';
      } catch (e) {}
    }

    _setFormVariant(variantId) {
      if (!variantId) return;
      if (this.idInput) this.idInput.value = variantId;

      // Also update any sibling form's primary id input (buy-buttons block) just in case.
      if (this.formId) {
        const forms = document.querySelectorAll('form[id="' + this.formId + '"]');
        forms.forEach((f) => {
          const id = f.querySelector('input[name="id"]');
          if (id) id.value = variantId;
        });
      }
    }

    _dispatchVariantChange(variant) {
      if (!this.formId || !variant) return;
      const form = document.getElementById(this.formId);
      if (!form) return;
      form.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: variant, product: null }
      }));
    }

    _updateATCAvailability(isAvailable) {
      if (!this.formId) return;
      // Submit buttons live inside <buy-buttons form="..."> wrappers (main ATC + sticky bar
      // + quick-buy template). Catch all of them.
      const buyButtonWraps = document.querySelectorAll('buy-buttons[form="' + this.formId + '"]');
      buyButtonWraps.forEach((wrap) => {
        const btn = wrap.querySelector('button[type="submit"]');
        if (!btn) return;
        // Save original label once so we can restore exact text.
        if (!btn._sscwOrigText) {
          btn._sscwOrigText = btn.textContent.trim() || 'Add to cart';
        }
        if (isAvailable) {
          btn.textContent = btn._sscwOrigText;
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('sscw-soldout');
        } else {
          btn.textContent = 'Sold out';
          btn.setAttribute('disabled', '');
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('sscw-soldout');
        }
      });
    }
  }

  customElements.define('sscw-picker', SscwPicker);
})();

/* Mobile section reorder is handled in ss-cookware-combo-tiles.css via flex
   order on main#main — no JS required. */
